// ABOUTME: Tour step definitions for the first-use application tour.
// ABOUTME: Defines the sequence and content of tour steps with interactive actions.

import type { Step, Placement } from 'react-joyride';

export interface TourStep extends Step {
  id: string;
  prerequisitePage?: string;
  waitForAction?: 'click-new-investigation' | 'click-send' | 'click-pin' | 'click-reports-nav' | 'click-new-report';
  hideNextButton?: boolean;
  hideCloseButton?: boolean;
  hideBackButton?: boolean;
}

export const tourSteps: TourStep[] = [
  // Step 1: Welcome
  {
    id: 'welcome',
    target: 'body',
    content: 'Welcome to Oleum BI! Let me give you a quick interactive tour.',
    placement: 'center' as Placement,
    disableBeacon: true,
    disableOverlayClose: true,
    spotlightClicks: false,
    styles: {
      options: {
        zIndex: 10000,
      },
    },
  },

  // Step 2: Navigation Overview
  {
    id: 'navigation',
    target: '[data-tour="navigation"]',
    content: 'Use these tabs to navigate between Home, Investigations, and Reports.',
    placement: 'bottom' as Placement,
    disableBeacon: true,
  },

  // Step 3: Investigations Tab
  {
    id: 'investigations-intro',
    target: '[data-tour="nav-investigations"]',
    content: 'Investigations are AI-powered conversations where you explore your data.',
    placement: 'bottom' as Placement,
    disableBeacon: true,
  },

  // Step 4: Reports Tab
  {
    id: 'reports-intro',
    target: '[data-tour="nav-reports"]',
    content: 'Reports let you compile insights into shareable documents.',
    placement: 'bottom' as Placement,
    disableBeacon: true,
  },

  // Step 5: Click New Investigation (interactive)
  {
    id: 'click-new-investigation',
    target: '[data-tour="new-investigation"]',
    content: 'Click the "New Investigation" button to continue',
    placement: 'right' as Placement,
    disableBeacon: true,
    prerequisitePage: '/investigations',
    spotlightClicks: true,
    hideCloseButton: true,
    hideNextButton: true,
    waitForAction: 'click-new-investigation',
  },

  // Step 6: Click Send button
  {
    id: 'click-send',
    target: '[data-tour="send-button"]',
    content: 'Click the send button to see a sample AI insight',
    placement: 'top' as Placement,
    disableBeacon: true,
    spotlightClicks: true,
    hideCloseButton: true,
    hideNextButton: true,
    hideBackButton: true,
    waitForAction: 'click-send',
  },

  // Step 7: Response received - user clicks Next to continue
  {
    id: 'response-received',
    target: '[data-tour="messages-area"]',
    content: 'Great! The AI has analyzed your request and generated a response. Click Next to learn about insights.',
    placement: 'left' as Placement,
    disableBeacon: true,
    spotlightClicks: false,
    hideBackButton: true,
  },

  // Step 8: Explain what an insight is (highlight the whole card)
  {
    id: 'insight-explanation',
    target: '[data-tour="insight-card"]',
    content: 'This is an Insight - a self-contained piece of analysis that the AI generates. Insights can be tables, charts, summaries, or other visualizations. They represent key findings from your data that you can save and use in reports.',
    placement: 'left' as Placement,
    disableBeacon: true,
    spotlightClicks: false,
    hideBackButton: true,
  },

  // Step 9: Pin the insight (interactive)
  {
    id: 'click-pin',
    target: '[data-tour="pin-button"]',
    content: 'Click the pin button to save this insight to your Pinboard',
    placement: 'left' as Placement,
    disableBeacon: true,
    spotlightClicks: true,
    hideCloseButton: true,
    hideNextButton: true,
    hideBackButton: true,
    waitForAction: 'click-pin',
  },

  // Step 10: Pinboard explanation
  {
    id: 'pinboard-toggle',
    target: '[data-tour="pinboard-toggle"]',
    content: 'Your pinned insights live here in the Pinboard. Use it to collect the most important findings from your investigations.',
    placement: 'bottom' as Placement,
    disableBeacon: true,
    hideBackButton: true,
  },

  // Step 11: Highlight the pinned insight in the pinboard
  {
    id: 'pinboard-insight',
    target: '[data-tour="pinboard-item"]',
    content: 'Here\'s your pinned insight! You can view, manage, and use pinned insights to build reports. Pins are organized by the investigation they came from.',
    placement: 'left' as Placement,
    disableBeacon: true,
    hideBackButton: true,
  },

  // Step 12: Click Reports nav tab
  {
    id: 'click-reports-nav',
    target: '[data-tour="nav-reports"]',
    content: 'Click the "Reports" tab now to create a report from your pinned insight',
    placement: 'bottom' as Placement,
    disableBeacon: true,
    spotlightClicks: true,
    hideCloseButton: true,
    hideNextButton: true,
    hideBackButton: true,
    waitForAction: 'click-reports-nav',
  },

  // Step 13: Click New Report button
  {
    id: 'click-new-report',
    target: '[data-tour="new-report"]',
    content: 'Click the "New Report" button now to continue',
    placement: 'left' as Placement,
    disableBeacon: true,
    prerequisitePage: '/reports',
    spotlightClicks: true,
    hideCloseButton: true,
    hideNextButton: true,
    hideBackButton: true,
    waitForAction: 'click-new-report',
  },

  // Step 14: Report wizard - explain investigations
  {
    id: 'report-explain-investigation',
    target: '[data-tour="wizard-investigations"]',
    content: 'Reports are built from your investigations. When you select an investigation, the AI uses the full conversation history as context to generate a comprehensive report.',
    placement: 'right' as Placement,
    disableBeacon: true,
    spotlightClicks: true,
    hideBackButton: true,
  },

  // Step 15: Report wizard - select investigation
  {
    id: 'report-select-investigation',
    target: '[data-tour="wizard-investigations"]',
    content: 'Select an investigation to include in your report. You can select multiple investigations to combine insights from different analyses.',
    placement: 'right' as Placement,
    disableBeacon: true,
    spotlightClicks: true,
    hideBackButton: true,
  },

  // Step 16: Report wizard - select pin
  {
    id: 'report-select-pin',
    target: '[data-tour="wizard-pins"]',
    content: 'Next, select your pinned insights. These are guaranteed to appear in your final report - perfect for must-have findings.',
    placement: 'right' as Placement,
    disableBeacon: true,
    spotlightClicks: true,
    hideBackButton: true,
  },

  // Step 17: Report wizard - next button
  {
    id: 'report-next',
    target: '[data-tour="wizard-next"]',
    content: 'Click Next to configure your report settings like length and tone, then generate it with AI!',
    placement: 'top' as Placement,
    disableBeacon: true,
    spotlightClicks: false,
    hideBackButton: true,
    hideNextButton: false,
  },

  // Step 18: Tour Complete
  {
    id: 'tour-complete',
    target: '[data-tour="help-button"]',
    content: "You're all set! We've cleaned up the demo data so you can start fresh. Click this help button anytime to restart the tour. Now go explore your data!",
    placement: 'top-end' as Placement,
    disableBeacon: true,
    spotlightClicks: false,
    hideBackButton: true,
  },
];

export const TOTAL_STEPS = tourSteps.length;

// Helper to get step index by ID
export const getStepIndex = (id: string): number => {
  return tourSteps.findIndex(step => step.id === id);
};
